import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import BackLink from "@/components/BackLink";
import { Calendar, MapPin, Mail, Phone } from "lucide-react";
import AndringsforslagButton from "@/components/AndringsforslagButton";
import ShareWidget from "@/components/ShareWidget";
import GalleriStrip from "@/components/GalleriStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['januari','februari','mars','april','maj','juni','juli','augusti','september','oktober','november','december'];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

const SLUG_MAP = {
  "Hjälp i vardagen": "hjalp-i-vardagen",
  "Hand mot hand": "hand-mot-hand",
  "Köp & sälj lokalt": "kop-salj",
};

const TAG_COLORS = {
  "Hjälp i vardagen": "bg-pine/10 text-pine",
  "Hand mot hand": "bg-falu/10 text-falu",
  "Köp & sälj lokalt": "bg-amber-100 text-amber-800",
};

export default function AnslagDetail() {
  const { id } = useParams();
  const [anslag, setAnslag] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/anslag/${id}`)
      .then(r => { setAnslag(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-14"><p className="text-muted-foreground">Laddar...</p></div>;
  if (!anslag || anslag.error) return <div className="max-w-3xl mx-auto px-4 py-14"><p className="text-muted-foreground">Anslaget hittades inte.</p></div>;

  const backSlug = SLUG_MAP[anslag.rubrik] || "";
  const tagColor = TAG_COLORS[anslag.rubrik] || "bg-bone-alt text-muted-foreground";

  const metaParts = [];
  if (anslag.publiceringsdatum) metaParts.push(formatDate(anslag.publiceringsdatum));
  if (anslag.ort) metaParts.push(anslag.ort);
  if (anslag.rubrik === "Köp & sälj lokalt") {
    if (anslag.pristyp === "belopp" && anslag.prisbelopp) metaParts.push(anslag.prisbelopp);
    else if (anslag.pristyp) metaParts.push(anslag.pristyp);
  }

  return (
    <div data-testid="anslag-detail-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <BackLink to={`/anslagstavla/${backSlug}`} label={anslag.rubrik} />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className={`inline-block text-sm font-body font-medium px-2.5 py-1 rounded-sm ${tagColor}`}>
          {anslag.rubrik}
        </span>
        {anslag.exempel && (
          <span className="inline-block text-xs font-body font-medium px-2 py-0.5 rounded-sm bg-amber-100 text-amber-700">
            Exempelanslag
          </span>
        )}
        {anslag.giltigt_tom && (() => {
          const today = new Date().toISOString().slice(0, 10);
          return anslag.giltigt_tom < today ? (
            <span data-testid="badge-expired" className="inline-block text-xs font-body font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-falu/10 text-falu">
              Utgånget ({anslag.giltigt_tom})
            </span>
          ) : (
            <span data-testid="badge-valid-until" className="inline-block text-xs font-body text-bark/60 italic">
              Giltigt t.o.m. {anslag.giltigt_tom}
            </span>
          );
        })()}
      </div>

      <h1 className="font-fraktur-smooth text-xl sm:text-2xl lg:text-3xl text-falu tracking-wide mb-4 leading-[1.1]">{anslag.titel}</h1>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mb-6">
        {metaParts.length > 0 && (
          <span className="flex items-center gap-1">
            <Calendar size={14} /> {metaParts.join(" \u00B7 ")}
          </span>
        )}
      </div>

      {/* Build 96.23 — bilder klickbara/vergrößerbara via befintlig GalleriStrip-lightbox.
          Video-URLs hanteras internt av GalleriStrip (spelas upp inline, ej lightbox). */}
      {(() => {
        const media = Array.isArray(anslag.galleri) && anslag.galleri.length > 0
          ? anslag.galleri
          : (anslag.bild_url ? [anslag.bild_url] : []);
        if (media.length === 0) return null;
        return (
          <div data-testid="anslag-media" className="mb-6">
            <GalleriStrip bilder={media} alt={anslag.titel} />
          </div>
        );
      })()}

      <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 mb-8">
        <p className="text-lg text-ink leading-relaxed whitespace-pre-line">{anslag.beskrivning}</p>
      </div>

      <div data-testid="contact-section" className="bg-bone-alt rounded-sm p-6 mb-6">
        <h2 className="font-heading text-lg font-medium text-ink mb-3">Kontakta {anslag.avsandare_namn}</h2>
        <div className="flex flex-col gap-2">
          {anslag.kontakt_epost && (
            <a href={`mailto:${anslag.kontakt_epost}`} className="inline-flex items-center gap-2 text-base text-falu hover:text-falu-hover no-underline">
              <Mail size={18} /> {anslag.kontakt_epost}
            </a>
          )}
          {anslag.kontakt_telefon && (
            <a href={`tel:${anslag.kontakt_telefon.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 text-base text-falu hover:text-falu-hover no-underline">
              <Phone size={18} /> {anslag.kontakt_telefon}
            </a>
          )}
        </div>
      </div>

      <p className="text-sm text-muted-foreground">Granskat av vidalv.live-teamet</p>

      <div className="mt-6 flex flex-wrap items-center gap-3 pt-4 border-t border-warm-border/60">
        <ShareWidget
          title={anslag.titel}
          text={anslag.beskrivning}
          path={`/anslag/${anslag.id}`}
        />
        <AndringsforslagButton typ="anslag" referensId={anslag.id} referensNamn={anslag.titel} />
      </div>
    </div>
  );
}
